// import helpers
import { useAuthStore } from '@bv/stores/auth';
import { useUserStore } from '@bv/stores/user';
import { io, Socket } from 'socket.io-client';

import { getItem } from '@/helpers/cookies/cookies.helper';
import { useNotificationsStore } from '@/stores/notifications/notifications.store';

import addInboundCall from './events/add-inbound-calls';
import conversationAssigned from './events/conversation_assigned';
import deleteInboundCall from './events/delete-inbound-calls';
import importContact from './events/import_contact';
import newMessageAssignedConversation from './events/new_message_assigned_conversation';
import updateUserStatus from './events/update_user_status';
import userMention from './events/user_mention';

interface InitOptions {
  options?: Partial<SocketOptions>;
}

interface SocketOptions {
  autoConnect: boolean;
  reconnection: boolean;
  reconnectionAttempts: number;
  [key: string]: any;
}

type Callback = (data: any) => void;

export class NotificationsSocketHandler {
  private socket: Socket | null = null;
  readonly socketUrl: string = import.meta.env.VITE_SOCKET_URL + '/notifications';
  // list of all events name
  private readonly events: string[] = [
    'new_message_assigned_conversation',
    'user_mention',
    'conversation_assigned',
    'import_contact',
    'USER.STATUS_UPDATED',
    'CALL_CENTER.CALL.STARTED',
    'CALL_CENTER.CALL.ENDED',
    'CALL_CENTER.CALL.FAILED',
  ];
  // list of all events callback methods
  private callbacks: Record<string, Callback> = {
    new_message_assigned_conversation: newMessageAssignedConversation,
    user_mention: userMention,
    conversation_assigned: conversationAssigned,
    import_contact: importContact,
    'USER.STATUS_UPDATED': updateUserStatus,
    'CALL_CENTER.CALL.STARTED': addInboundCall,
    'CALL_CENTER.CALL.ENDED': deleteInboundCall,
    'CALL_CENTER.CALL.FAILED': deleteInboundCall,
  };
  private notificationsStore: ReturnType<typeof useNotificationsStore> | null = null;
  private useUserStore: ReturnType<typeof useUserStore> | null = null;

  // reconnection retry
  private connectionRetryCount: number = 0;
  private readonly maxRetries: number = 5;

  init({ options = {} }: InitOptions = {}): void {
    if (this.socket) {
      return;
    } // Prevent re-init

    this.notificationsStore = useNotificationsStore();
    this.useUserStore = useUserStore();

    this.socket = io(`${this.socketUrl}`, {
      transports: ['websocket'], // <--- Force WebSocket only
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: this.maxRetries,
      reconnectionDelay: 1000, // ms between tries
      timeout: 20000, // connection timeout
      auth: {
        token:
          (import.meta.env.VITE_IMPERSONATE === 'true' &&
            (useAuthStore().impersonateToken || getItem('impersonate_token'))) ||
          useAuthStore().token ||
          getItem('token'),
        workspaceId: this.useUserStore?.currentWorkspace?.id,
      },
      ...options,
    });

    this.setupConnectionListeners();
    this.registerEventListeners();
  }

  private setupConnectionListeners(): void {
    (this.socket as Socket).on('connect', this.onOpen.bind(this));
    (this.socket as Socket).on('disconnect', this.onClose.bind(this));
    (this.socket as Socket).on('connect_error', this.onError.bind(this));
  }

  private registerEventListeners(): void {
    this.events.forEach((eventName: string) => {
      (this.socket as Socket).on(eventName, (data: any) => this.onEvent(eventName, data));
    });
  }

  private onEvent(eventName: string, data: any): void {
    if (this.callbacks[eventName]) {
      if (['conversation_assigned', 'USER.STATUS_UPDATED'].includes(eventName)) {
        this.callbacks[eventName]({ data });
      } else {
        this.callbacks[eventName]({ ...data.body, timestamp: data.timestamp });
      }
    } else {
      // logic here
    }
  }

  private onOpen(): void {
    this.notificationsStore?.toggleSocketConnection();
    this.connectionRetryCount = 0;
  }

  private onClose(): void {
    this.notificationsStore?.toggleSocketConnection();
  }

  reconnectionAttempt(): void {
    if (this.socket) {
      if (this.socket.connected) {
        return;
      }

      this.connectionRetryCount = 0;
      this.notificationsStore?.toggleSocketReconnectionPopup();
      this.socket.disconnect(); // force reset internal state
      this.socket.connect(); // now reconnect from scratch
    } else {
      this.init(); // recreate from scratch if no socket
    }
  }

  private onError(error: any): void {
    if (++this.connectionRetryCount === this.maxRetries) {
      this.notificationsStore?.saveSocketError(error);
      this.notificationsStore?.toggleSocketReconnectionPopup();
    }
  }

  on(eventName: string, callback: Callback): void {
    this.callbacks[eventName] = callback;
  }

  off(eventName: string): void {
    delete this.callbacks[eventName];
    (this.socket as Socket).off(eventName);
  }

  sendAction(name: string, data: any): void {
    if (!this.socket) {
      return;
    }
    this.socket.emit(name, data);
  }

  destroy(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }
}

const NotificationsSocket = new NotificationsSocketHandler();

export default NotificationsSocket;
